import { useCompanyContext } from "@/features/company/hooks/use-company-context";
import {
  useMutation,
  type UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions
} from "@tanstack/react-query";
import { DocumentApiClient } from "../api-client/api-client";
import type { Document, LegalDocument, ResponseUpdateKyc, UploadDocument } from "../types";
import { QueryOptionsFactory } from "@/features/company/utils";
import { StandardResponse } from "@/features/utils/types";

export const useDocument = () => {
  const { leUuid } = useCompanyContext();
  const queryClient = useQueryClient();

  const DocumentsLegalQuery = (options?: UseQueryOptions<LegalDocument | null, null>) =>
    useQuery({
      queryKey: ["documents", leUuid],
      queryFn: async () => {
        if (!leUuid) return null;
        return await DocumentApiClient.getDocumentsChartCompany(leUuid);
      },
      enabled: !!leUuid,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: false,
      staleTime: 1 * 60 * 1000,
      ...options
    });

  const DocumentsDetailsQuery = (UUID: string | null, enable?: boolean) =>
    useQuery({
      queryKey: ["user", "profile", "documents", "details", UUID],
      queryFn: async () => {
        if (!leUuid && !UUID) return null;
        return await DocumentApiClient.getDocumentsDetails(UUID ?? "", leUuid);
      },
      enabled: !!UUID || enable
    });
  const DocumentsDetailsMutation = (
    options?: UseMutationOptions<
      Document[],
      Error,
      {
        UUID: string;
        isAgentLe?: boolean;
      }
    >
  ) =>
    useMutation<Document[], Error, { UUID: string; isAgentLe?: boolean }>({
      mutationFn: async (data) =>
        await DocumentApiClient.getDocumentsDetails(data.UUID ?? "", leUuid, data.isAgentLe),
      ...options
    });

  const DocumentDetailsQuery = (UUID: string | null, algoregDocumentId: string) =>
    useQuery({
      queryKey: ["user", "profile", "document", "details", algoregDocumentId],
      queryFn: async () => {
        await DocumentApiClient.getDocumentDetail(
          UUID ?? "",
          algoregDocumentId,
          leUuid ?? undefined
        );
      },
      enabled: !!UUID
    });
  const DocumentQuery = (UUID: string | null, algoregDocumentId: string) =>
    useQuery({
      queryKey: ["user", "profile", "document", algoregDocumentId],
      queryFn: () =>
        DocumentApiClient.getDocumentContent(UUID ?? "", algoregDocumentId, leUuid as string),
      enabled: !!UUID
    });
  const DocumentDownloadMutation = (
    options?: UseMutationOptions<
      Blob,
      Error,
      { UUID: string | null; algoregDocumentId: string; isAgentLe?: boolean }
    >
  ) =>
    useMutation<
      Blob,
      Error,
      { UUID: string | null; algoregDocumentId: string; isAgentLe?: boolean }
    >({
      mutationFn: async (data) =>
        await DocumentApiClient.getDocumentContent(
          data.UUID ?? "",
          data.algoregDocumentId,
          (leUuid as string) ?? undefined,
          data.isAgentLe
        ),
      ...options
    });
  const DocumentTypesQuery = () =>
    useQuery({
      queryKey: ["user", "profile", "documents", "types"],
      queryFn: async () => await DocumentApiClient.getDocumentTypes()
    });

  const UploadDocumentMutation = (
    options?: UseMutationOptions<
      { session_id: string },
      Error,
      UploadDocument & { isAgent?: boolean }
    >
  ) =>
    useMutation<{ session_id: string }, Error, UploadDocument>({
      mutationFn: async (doc: UploadDocument) =>
        DocumentApiClient.uploadDocument(doc, leUuid as string),
      ...options
    });
  const UploadDocumentChartMutation = (
    options?: UseMutationOptions<{ success: boolean }, Error, LegalDocument>
  ) =>
    useMutation<{ success: boolean }, Error, LegalDocument>({
      mutationFn: async (doc: LegalDocument) => {
        return await DocumentApiClient.uploadDocumentChart(doc, leUuid ?? "");
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: QueryOptionsFactory.legalEntity(leUuid ?? "").queryKey
        });
      },
      ...options
    });
  const updateUserUnderAuditMutation = (
    options?: UseMutationOptions<StandardResponse<ResponseUpdateKyc>, Error>
  ) =>
    useMutation<StandardResponse<ResponseUpdateKyc>, Error>({
      mutationFn: async () => DocumentApiClient.updateUserUnderAudit(),
      onSettled: () => queryClient.invalidateQueries({ queryKey: ["user", "profile"] }),
      ...options
    });
  return {
    DocumentsDetailsQuery,
    DocumentDetailsQuery,
    DocumentQuery,
    DocumentTypesQuery,
    UploadDocumentMutation,
    updateUserUnderAuditMutation,
    UploadDocumentChartMutation,
    DocumentsLegalQuery,
    DocumentDownloadMutation,
    DocumentsDetailsMutation
  };
};
